import { MouseEvent, useState } from 'react';
import { OutlineMailIcon } from '@tempmee/react-icons';
import { Button } from '@tempmee/react';
import { colors } from '@tempmee/tailwind';
import { Center, Pressable } from '~/commonComponents';
import Portal from '~/commonComponents/Portal';

import { IShift } from '~/models';
import {
  fireShiftsOverviewInviteClicked,
  fireShiftDetailsInviteClicked,
  fireScheduleListInviteClicked,
  type TInvitePopupSource,
} from '~/posthogs/events';
import { type InviteSource } from '~/utils/posthog/inviteTracking';
import { InviteProfessionalPopup } from './Popup';

// InviteProfessional is used in Shifts/Schedule contexts
type TInviteProfessionalSource = Extract<
  InviteSource,
  'schedule_list' | 'shifts_open' | 'shifts_action_needed' | 'shift_details'
>;

const sourceToTrackingSource: Record<string, TInvitePopupSource> = {
  shifts_open: 'Open Shift',
  shifts_action_needed: 'Action Needed',
  shift_details: 'Shift Details',
  schedule_list: 'Schedule List',
};

interface IProps {
  shift: IShift;
  buttonText?: string;
  isRefetchJob?: boolean;
  size?: 'icon' | 'default';
  source?: TInviteProfessionalSource;
  onInviteSuccess?: () => void;
}

export const InviteProfessional = ({
  shift,
  buttonText = 'Invite Professionals',
  isRefetchJob = true,
  size = 'default',
  source = 'shifts_open',
  onInviteSuccess,
}: IProps) => {
  const [isInvitePopupOpen, setInvitePopupOpen] = useState(false);

  const handleInviteClick = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setInvitePopupOpen(true);

    const shiftId = shift?.id || '';

    if (source === 'shifts_open' || source === 'shifts_action_needed') {
      fireShiftsOverviewInviteClicked({
        shiftStatus: source === 'shifts_action_needed' ? 'Action Needed' : 'Open',
        shiftId,
      });
    } else if (source === 'shift_details') {
      fireShiftDetailsInviteClicked({ shiftId });
    } else if (source === 'schedule_list') {
      fireScheduleListInviteClicked({ shiftId });
    }
  };

  return (
    <>
      <Center width="100%" height="100%">
        {size === 'default' ? (
          <Button
            LeadingIcon={<OutlineMailIcon size={20} />}
            data-cy="invite-professional-trigger"
            size="lg"
            onClick={handleInviteClick}
          >
            {buttonText}
          </Button>
        ) : (
          <Pressable onPress={handleInviteClick} dataCy="invite-professional-trigger">
            <OutlineMailIcon size={24} color={colors.secondary[500]} />
          </Pressable>
        )}
      </Center>
      {isInvitePopupOpen && (
        <Portal>
          <div onClick={(e) => e.stopPropagation()}>
            <InviteProfessionalPopup
              isOpen={isInvitePopupOpen}
              isRefetchJob={isRefetchJob}
              jobId={shift?.id}
              profession={shift?.profession}
              officeId={shift?.offer_owner?.id}
              onClose={() => setInvitePopupOpen(false)}
              onInviteSuccess={onInviteSuccess}
              trackingSource={sourceToTrackingSource[source]}
            />
          </div>
        </Portal>
      )}
    </>
  );
};
